import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';

// ============================================
// Cursor State Types
// ============================================

type CursorState = 'default' | 'pointer' | 'loading' | 'hover' | 'text' | 'hidden';

// ============================================
// Custom Cursor Hook
// ============================================

export const useCustomCursor = () => {
  const [cursorState, setCursorState] = useState<CursorState>('default');
  const [isVisible, setIsVisible] = useState(true);
  const [hoverText, setHoverText] = useState<string | null>(null);

  const setCursor = useCallback((state: CursorState, text?: string) => {
    setCursorState(state);
    setHoverText(text || null);
  }, []);

  const resetCursor = useCallback(() => {
    setCursorState('default');
    setHoverText(null);
  }, []);

  return {
    cursorState,
    isVisible,
    hoverText,
    setCursor,
    resetCursor,
    setIsVisible,
  };
};

// ============================================
// Custom Cursor Component
// ============================================

interface CustomCursorProps {
  color?: string;
  size?: number;
}

export const CustomCursor: React.FC<CustomCursorProps> = ({
  color = '#3b82f6',
  size = 20,
}) => {
  const [cursorState, setCursorState] = useState<CursorState>('default');
  const [hoverText, setHoverText] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);

  // Motion values for smooth cursor movement
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);

  // Spring animation for smooth following
  const springConfig = { damping: 25, stiffness: 400 };
  const cursorXSpring = useSpring(cursorX, springConfig);
  const cursorYSpring = useSpring(cursorY, springConfig);

  // Trail cursor (larger, slower)
  const trailX = useSpring(cursorX, { damping: 50, stiffness: 200 });
  const trailY = useSpring(cursorY, { damping: 50, stiffness: 200 });

  useEffect(() => {
    // Check if device has a mouse
    const hasTouch = 'ontouchstart' in window;
    if (hasTouch) return;

    const moveCursor = (e: MouseEvent) => {
      cursorX.set(e.clientX);
      cursorY.set(e.clientY);
      setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = () => {
      setIsVisible(true);
    };

    // Detect interactive elements
    const handleElementHover = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check for clickable elements
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.closest('button') ||
        target.closest('a') ||
        target.getAttribute('role') === 'button' ||
        target.classList.contains('cursor-pointer')
      ) {
        setCursorState('pointer');
        return;
      }

      // Check for text inputs
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('contenteditable') === 'true'
      ) {
        setCursorState('text');
        return;
      }

      // Check for custom data attributes
      const cursorType = target.getAttribute('data-cursor');
      if (cursorType) {
        setCursorState(cursorType as CursorState);
        const text = target.getAttribute('data-cursor-text');
        if (text) setHoverText(text);
        return;
      }

      setCursorState('default');
      setHoverText(null);
    };

    window.addEventListener('mousemove', moveCursor);
    window.addEventListener('mouseover', handleElementHover);
    document.body.addEventListener('mouseleave', handleMouseLeave);
    document.body.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mouseover', handleElementHover);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
      document.body.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [cursorX, cursorY]);

  // Don't render on touch devices
  if (typeof window !== 'undefined' && 'ontouchstart' in window) {
    return null;
  }

  const getCursorSize = () => {
    switch (cursorState) {
      case 'pointer':
        return size * 2;
      case 'hover':
        return size * 3;
      case 'text':
        return 2;
      case 'loading':
        return size * 1.5;
      default:
        return size;
    }
  };

  return (
    <>
      {/* Hide default cursor */}
      <style>{`
        * {
          cursor: none !important;
        }
      `}</style>

      {/* Trail cursor (outer ring) */}
      <motion.div
        ref={cursorRef}
        className="fixed top-0 left-0 pointer-events-none z-[9999] mix-blend-difference"
        style={{
          x: trailX,
          y: trailY,
          translateX: '-50%',
          translateY: '-50%',
        }}
      >
        <AnimatePresence>
          {isVisible && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: cursorState === 'loading' ? 0.3 : 0.15,
                width: getCursorSize() * 2,
                height: getCursorSize() * 2,
              }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="rounded-full border-2"
              style={{ borderColor: color }}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Main cursor (dot) */}
      <motion.div
        className="fixed top-0 left-0 pointer-events-none z-[9999]"
        style={{
          x: cursorXSpring,
          y: cursorYSpring,
          translateX: '-50%',
          translateY: '-50%',
        }}
      >
        <AnimatePresence mode="wait">
          {isVisible && (
            <motion.div
              key={cursorState}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {cursorState === 'loading' ? (
                // Loading spinner
                <motion.div
                  className="rounded-full border-2 border-transparent"
                  style={{
                    width: getCursorSize(),
                    height: getCursorSize(),
                    borderTopColor: color,
                    borderRightColor: color,
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                />
              ) : cursorState === 'text' ? (
                // Text cursor (I-beam)
                <motion.div
                  className="bg-white"
                  style={{
                    width: 2,
                    height: size * 1.2,
                  }}
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              ) : (
                // Default dot cursor
                <motion.div
                  className="rounded-full"
                  style={{
                    width: getCursorSize(),
                    height: getCursorSize(),
                    backgroundColor: color,
                  }}
                  animate={
                    cursorState === 'pointer'
                      ? {
                          scale: [1, 1.1, 1],
                        }
                      : {}
                  }
                  transition={{ duration: 0.5, repeat: Infinity }}
                />
              )}

              {/* Hover text label */}
              <AnimatePresence>
                {hoverText && (
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 20 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 bg-zinc-900 text-white text-xs rounded-md"
                  >
                    {hoverText}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

// ============================================
// Cursor Context for Global State
// ============================================

interface CursorContextType {
  setCursor: (state: CursorState, text?: string) => void;
  resetCursor: () => void;
}

const CursorContext = React.createContext<CursorContextType | null>(null);

export const CursorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [, setCursorState] = useState<CursorState>('default');
  const [, setHoverText] = useState<string | null>(null);

  const setCursor = useCallback((state: CursorState, text?: string) => {
    setCursorState(state);
    setHoverText(text || null);
  }, []);

  const resetCursor = useCallback(() => {
    setCursorState('default');
    setHoverText(null);
  }, []);

  return (
    <CursorContext.Provider value={{ setCursor, resetCursor }}>
      {children}
      <CustomCursor />
    </CursorContext.Provider>
  );
};

export const useCursor = () => {
  const context = React.useContext(CursorContext);
  if (!context) {
    return {
      setCursor: () => {},
      resetCursor: () => {},
    };
  }
  return context;
};

export default CustomCursor;
